import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import NINVerification from './components/NINVerification';
import { ShieldCheck, Zap, BadgeCheck } from 'lucide-react';
import { io } from 'socket.io-client';
import Layout from './components/Layout';
import MarketsPage from './pages/MarketsPage';
import LeaguePage from './pages/LeaguePage';
import StorePage from './pages/StorePage';
import ProfilePage from './pages/ProfilePage';
import LeaderboardPage from './pages/LeaderboardPage';
import AuthPage from './pages/AuthPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

// ── Auth Context ──────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);
export const SocketContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export const useSocket = () => useContext(SocketContext);

const API = 'https://api.naijapredict.net';

export function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('np_token');
  return fetch(API + path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(async r => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Request failed');
    return data;
  });
}

function LeagueModal({ onClose, onJoin }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.88)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)', padding:'20px' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'var(--bg1)', borderRadius:24, width:'100%', maxWidth:480, border:'1px solid var(--border)', maxHeight:'90vh', overflowY:'auto', animation:'slideUp .3s ease', margin:'0 auto 0' }}>

        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#ff6b35,#f5c842)', padding:'28px 20px 20px', textAlign:'center', position:'relative' }}>
          <div style={{ fontSize:40, marginBottom:8 }}>🏆</div>
          <div style={{ fontWeight:900, fontSize:22, color:'#000', letterSpacing:'.5px', lineHeight:1.2 }}>PREDICTION LEAGUE</div>
          <div style={{ fontSize:13, color:'rgba(0,0,0,.7)', marginTop:4, fontWeight:600 }}>NaijaPredict just got bigger!</div>
          <button onClick={onClose} style={{ position:'absolute', top:12, right:12, background:'rgba(0,0,0,.15)', border:'none', color:'#000', borderRadius:'50%', width:28, height:28, fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding:'20px' }}>
          <p style={{ fontSize:13, color:'var(--text2)', textAlign:'center', lineHeight:1.7, marginBottom:16 }}>
            Every week, we open a competition.<br/>Pay ₦1,000 to enter. Predict markets. Win big!
          </p>

          {/* Steps */}
          {[
            ['💳', 'Pay ₦1,000 entry fee', 'Get 100 ⭐ Stars credited instantly (worth ₦1,500)'],
            ['🎯', 'Predict on any market', 'Every correct prediction earns you points'],
            ['🏆', 'Top predictors win prizes', 'Weekly prize pool shared among top 3'],
          ].map(([icon,title,sub]) => (
            <div key={title} style={{ display:'flex', gap:14, alignItems:'flex-start', marginBottom:14 }}>
              <div style={{ width:40, height:40, borderRadius:12, background:'rgba(255,107,53,.1)', border:'1px solid rgba(255,107,53,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{icon}</div>
              <div>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:2 }}>{title}</div>
                <div style={{ fontSize:11, color:'var(--text2)' }}>{sub}</div>
              </div>
            </div>
          ))}

          {/* Prizes */}
          <div style={{ background:'rgba(245,200,66,.06)', border:'1px solid rgba(245,200,66,.2)', borderRadius:14, padding:'12px 14px', marginBottom:16, textAlign:'center' }}>
            <div style={{ fontSize:11, color:'#f5c842', fontWeight:700, letterSpacing:'.5px', marginBottom:8 }}>THIS WEEK'S PRIZES</div>
            <div style={{ display:'flex', justifyContent:'space-around' }}>
              {[['🥇','1st','50%'],['🥈','2nd','30%'],['🥉','3rd','15%']].map(([medal,place,pct]) => (
                <div key={place} style={{ textAlign:'center' }}>
                  <div style={{ fontSize:20 }}>{medal}</div>
                  <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>{place} place</div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#f5c842' }}>{pct} of pool</div>
                </div>
              ))}
            </div>
          </div>

          {/* Note */}
          <div style={{ background:'rgba(255,107,53,.05)', border:'1px solid rgba(255,107,53,.15)', borderRadius:10, padding:'10px 14px', marginBottom:20, fontSize:11, color:'var(--text2)', textAlign:'center', lineHeight:1.6 }}>
            ⏰ League runs Monday–Sunday · Minimum 10 players · Unclaimed pool rolls over
          </div>

          {/* Buttons */}
          <button onClick={onJoin} style={{ width:'100%', background:'linear-gradient(135deg,#ff6b35,#f5c842)', border:'none', borderRadius:14, padding:'14px', fontSize:15, fontWeight:800, color:'#000', cursor:'pointer', marginBottom:10 }}>
            🏆 Join This Week's League
          </button>
          <button onClick={onClose} style={{ width:'100%', background:'transparent', border:'none', color:'var(--text2)', fontSize:13, cursor:'pointer', padding:'8px' }}>
            Maybe later
          </button>
        </div>
      </div>
      <style>{'@keyframes slideUp{from{transform:translateY(40px);opacity:0}to{transform:translateY(0);opacity:1}}'}</style>
    </div>
  );
}

export default function App() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);
  const [socket, setSocket]   = useState(null);
  const [theme, setTheme]           = useState(() => localStorage.getItem('np_theme')||'light');
  const [showLeagueModal, setShowLeagueModal] = useState(false);
  const [showNINGate, setShowNINGate] = useState(false);


  useEffect(() => {
    const lastSeen = parseInt(localStorage.getItem('np_league_modal_ts')||'0');
    const hoursSince = (Date.now() - lastSeen) / (1000*60*60);
    if (hoursSince >= 12) setTimeout(() => setShowLeagueModal(true), 2000);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('np_theme', theme);
  }, [theme]);

  function toggleTheme() { setTheme(t => t==='dark'?'light':'dark'); }
  function closeLeagueModal() { localStorage.setItem('np_league_modal_ts', Date.now().toString()); setShowLeagueModal(false); }



  useEffect(() => {
    const s = io('https://api.naijapredict.net', {
      transports: ['websocket','polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    s.on('connect', () => console.log('[Socket] Connected'));
    s.on('disconnect', () => console.log('[Socket] Disconnected'));
    // Balance update from server
    s.on('balance:update', ({ balance }) => {
      setUser(u => u ? { ...u, balance } : u);
    });
    setSocket(s);
    return () => s.disconnect();
  }, []);


  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  // Check maintenance mode
  const [maintenance, setMaintenance] = useState(false);

  async function loadMe() {
    try {
      const data = await apiFetch('/api/web/me');
      setUser(data);
      // NIN is optional - no forced gate
      setShowNINGate(false);



    } catch(e) {
      if (e.message && e.message.includes('maintenance')) {
        setMaintenance(true);
      } else {
        localStorage.removeItem('np_token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadMe(); }, []);

  function login(token, userData) {
    localStorage.setItem('np_token', token);
    setUser(userData);
    setShowNINGate(false); // NIN is now optional
  }

  async function logout() {
    try { await apiFetch('/api/web/logout', { method: 'POST' }); } catch(e) {}
    localStorage.removeItem('np_token');
    setUser(null);
  }

  if (maintenance) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16, padding:20, textAlign:'center' }}>
      <div style={{ fontSize:48 }}>🔧</div>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:32, letterSpacing:2, background:'linear-gradient(135deg,#ff6b35,#f5c842)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>NAIJAPREDICT</div>
      <div style={{ fontSize:18, fontWeight:700, color:'var(--text)' }}>Under Maintenance</div>
      <div style={{ fontSize:14, color:'var(--text2)', maxWidth:400 }}>We're making improvements to give you a better experience. We'll be back shortly!</div>
      <div style={{ fontSize:13, color:'var(--muted)' }}>In the meantime, use our Telegram bot: <a href="https://t.me/NaijaPredict_Bot" style={{ color:'#ff6b35' }}>@NaijaPredict_Bot</a></div>
    </div>
  );

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:12, background:'var(--bg)' }}>
      <img src="/logo.png" alt="NaijaPredict" style={{ width:72, height:72, borderRadius:16, marginBottom:4, animation:'fadeIn .5s ease' }}/>
      <div className="brand-text" style={{ fontSize:28 }}>NAIJAPREDICT</div>
      <div style={{ width:36, height:36, border:'3px solid var(--bg3)', borderTop:'3px solid #ff6b35', borderRadius:'50%', animation:'spin 1s linear infinite', marginTop:8 }}/>
    </div>
  );

  return (
    <SocketContext.Provider value={socket}>
    <AuthContext.Provider value={{ user, setUser, login, logout, showToast, loadMe, theme, toggleTheme, showLeagueModal, setShowLeagueModal, closeLeagueModal }}>
      <BrowserRouter>
        {toast && (
          <div style={{
            position:'fixed', top:20, right:20, zIndex:9999,
            background: toast.type==='error'?'#ff4444': toast.type==='success'?'#00c566':'#1a2228',
            color:'#fff', padding:'12px 20px', borderRadius:12,
            fontSize:14, fontWeight:600, boxShadow:'0 8px 32px rgba(0,0,0,.4)',
            animation:'slideIn .3s ease'
          }}>
            {toast.msg}
          </div>
        )}
        <style>{'@keyframes slideIn{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}'}</style>
        <Routes>
          <Route path="/auth" element={user ? <Navigate to="/" /> : <AuthPage />} />
          <Route element={<Layout />}>
            <Route path="/"           element={<MarketsPage />} />
            <Route path="/league"     element={<LeaguePage />} />
            <Route path="/store"      element={user ? <StorePage /> : <Navigate to="/auth" />} />
            <Route path="/profile"    element={user ? <ProfilePage /> : <Navigate to="/auth" />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      {/* NIN Verification Gate — slides up like league modal, cannot be dismissed */}
      {showNINGate && user && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.88)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center', backdropFilter:'blur(6px)' }}>
          <div style={{ background:'var(--bg1)', borderRadius:'24px 24px 0 0', width:'100%', maxWidth:520, border:'1px solid var(--border)', borderBottom:'none', maxHeight:'92vh', overflowY:'auto', animation:'slideUp .35s ease' }}>

            {/* Header */}
            <div style={{ background:'linear-gradient(135deg,#ff6b35,#f5c842)', padding:'24px 20px 18px', textAlign:'center', position:'relative' }}>
              <div style={{ fontSize:36, marginBottom:6 }}>🪪</div>
              <div style={{ fontWeight:900, fontSize:20, color:'#000', letterSpacing:'.5px' }}>IDENTITY VERIFICATION</div>
              <div style={{ fontSize:12, color:'rgba(0,0,0,.7)', marginTop:3, fontWeight:600 }}>Required to access NaijaPredict</div>
            </div>

            {/* Body */}
            <div style={{ padding:'20px' }}>
              <div style={{ fontSize:12, color:'var(--text2)', textAlign:'center', lineHeight:1.7, marginBottom:16 }}>
                Nigerian law requires identity verification for financial platforms.<br/>
                Enter your NIN to continue. <strong>Dial *346#</strong> to retrieve it.
              </div>

              {/* Steps */}
              {[
                { Icon: ShieldCheck, title:'Secure & Private',  sub:'Verified directly via NIMC database' },
                { Icon: Zap,         title:'Takes 30 seconds',  sub:'Enter your NIN and confirm your identity' },
                { Icon: BadgeCheck,  title:'One-time only',     sub:'Verify once, access forever' },
              ].map(({ Icon, title, sub }) => (
                <div key={title} style={{ display:'flex', gap:12, alignItems:'center', marginBottom:10 }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:'rgba(255,107,53,.1)', border:'1px solid rgba(255,107,53,.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Icon size={18} color="var(--orange)" strokeWidth={2}/>
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:13 }}>{title}</div>
                    <div style={{ fontSize:11, color:'var(--text2)' }}>{sub}</div>
                  </div>
                </div>
              ))}

              <div style={{ height:1, background:'var(--border)', margin:'16px 0' }}/>

              <NINVerification embedded={true} onVerified={() => { setShowNINGate(false); setUser(u => ({...u, ninVerified: true})); }}/>


            </div>
          </div>
          <style>{'@keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}'}</style>
        </div>
      )}

      {/* League Modal */}
      {showLeagueModal && <LeagueModal onClose={closeLeagueModal} onJoin={() => { closeLeagueModal(); window.location.href='/league'; }}/>}
      </BrowserRouter>
    </AuthContext.Provider>
    </SocketContext.Provider>
  );
}
